import { DateTime } from 'luxon'

import { isNotNull } from '@/utils/helpers'

import { filterAttendingLiveHelpSessions } from '@/queries/helpers'
import SessionQ from '@/queries/SessionQ'
import SessionRegistrationQ from '@/queries/SessionRegistrationQ'
import TopicQ from '@/queries/TopicQ'
import { SQL } from '@/client/query'
import composeRichText from '@/client/services/richText/composeRichText'
import getSessionUrl from '@/client/services/session/getSessionUrl'
import { hasUserRole } from '@/client/services/user'

import { makeBannerDataLoader } from './helpers'

export default makeBannerDataLoader({
  name: 'join-session',
  load(context) {
    if (!hasUserRole(context.user, 'user:approved')) {
      return null
    }
    const sessionWhere = SQL`(
      ${SessionQ.end_date} > NOW()
      AND (${SessionQ.start_date} - INTERVAL '1 hour') < NOW()
      AND ${filterAttendingLiveHelpSessions(context.user.id)}
    )
    ORDER BY ${SessionQ.start_date} ASC`

    const sessionsJoining = context.query
      .many(
        SessionRegistrationQ.where(
          {
            userId: context.user.id,
            includeSession: true,
            includeTopic: true,
          },
          sessionWhere
        )
      )
      .map(registration => ({
        session: registration,
        isUserTutor: false,
        isDropInUser: registration(SessionRegistrationQ.type) === 'series-dropin',
      }))

    const sessionsHosting = context.query
      .many(
        SessionQ.where(
          {
            tutor: context.user.id,
            include: [{ association: 'Topic', required: true }],
          },
          sessionWhere
        )
      )
      .map(session => ({ session, isUserTutor: true, isDropInUser: false }))

    const sessions = sessionsJoining.concat(sessionsHosting)

    const sessionsData = sessions
      .map(({ session, isDropInUser }) => ({
        sessionId: session(SessionQ.id) ?? 0,
        seriesId: session(SessionQ.series_id) ?? null,
        startDate: session(SessionQ.start_date) ?? '',
        topicName: `${session(TopicQ.name)}`,
        inProgress: new Date(`${session(SessionQ.start_date)}`) < new Date(),
        isDropInUser,
      }))
      .map(item => ({
        ...item,
        link: getSessionUrl(item),
      }))

    return sessionsData
      .map(session => ({
        slug: `join-session:${session.inProgress ? 'live' : 'upcoming'}:${session.sessionId}`,
        link: session.link,
        content: composeRichText(ctx =>
          session.inProgress
            ? ctx.paragraph(
                ctx.linkInternal(session.link, 'Your session'),
                ' in ',
                session.topicName,
                ' is happening now!'
              )
            : ctx.paragraph(
                ctx.linkInternal(session.link, 'Your session'),
                ' in ',
                session.topicName,
                ' starts ',
                ctx.dateTime(DateTime.fromISO(session.startDate), { isRelative: true })
              )
        ),
      }))
      .filter(isNotNull)
  },
})
